import React, { useState, useEffect} from 'react';
import Footer from './Footer';
import SymptomSearchText from './SymptomSearchText';
import SymptomSearchBody from './SymptomSearchBody';
import { getMarkDown, useDetectMobile, useNextButtonWidthClass, formatCardLoadTiming, getButtonWithArrow } from '../utils';
import { useTiming } from '../TimingContext';
import { useCardLoadTiming } from '../hooks/useCardLoadTiming';
import '../../../stylesheets/triagebot/SymptomSearch.scss';

const getUniqueSymptoms = (symptoms) => {
    const seenIds = new Set();

    return symptoms.filter(symptom => {
        if (seenIds.has(symptom.id)) return false;

        seenIds.add(symptom.id);
        return true;
    });
};

const MobileSymptomSearchToast = ({ initialTab, message, initialTags, onTagsChange, searchTab, bodyTab, bodyPartHover, setBodyPartHover, disabled, HoverColor, infermedicaAppIdKey, evisits, setevisits, setToastMessage, symptomError, hasOnlyInvalidSymptomsSelected }) => {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [tags, setTags] = useState(initialTags);

    const updateTags = (value) => {
        const uniqueTags = getUniqueSymptoms(value);
        setTags(uniqueTags);
        onTagsChange(uniqueTags);
    };

    return (
        <div className='symptom-search-toast'>
            <div className='tabbar'>
                <div
                    className={activeTab ? 'tab' : 'selected tab'}
                    onClick={() => setActiveTab(0)}
                >
                    {searchTab}
                </div>
                <div
                    className={activeTab ? 'selected tab' : 'tab'}
                    onClick={() => setActiveTab(1)}
                >
                    {bodyTab}
                </div>
                <button
                    id='iphone-focus-hack'
                    onClick={() => document.querySelector('.symptom-search-toast input')?.focus()}
                    style={{ visibility: 'hidden' }}
                ></button>
            </div>
            {
                activeTab
                    ? <SymptomSearchBody message={message} tags={tags} setTags={updateTags} bodyPartHover={bodyPartHover} setBodyPartHover={setBodyPartHover} disabled={disabled} HoverColor={HoverColor} infermedicaAppIdKey={infermedicaAppIdKey} />
                    : <SymptomSearchText message={message} tags={tags} setTags={updateTags} setBodyPartHover={setBodyPartHover} disabled={disabled} HoverColor={HoverColor} evisits={evisits} setevisits={setevisits} setToastMessage={setToastMessage} infermedicaAppIdKey={infermedicaAppIdKey} toastrClicked={true} symptomError={symptomError} hasOnlyInvalidSymptomsSelected={hasOnlyInvalidSymptomsSelected} />
            }
        </div>
    );
};

const SymptomSearchMessage = ({ message, sendMessage, disabled, setToastMessage, HoverColor, infermedicaAppIdKey, symptomError, setSymptomError }) => {
    const [submitted, setSubmitted] = useState(false);
    const [editing, setEditing] = useState(false);
    const [symptomsList, setSymptomsList] = useState([]);
    const [invalidSymptomsList, setInvalidSymptomsList] = useState([]);
    const [invalidSymptomsMessage, setInvalidSymptomsMessage] = useState('');
    const [bodyPartHover, setBodyPartHover] = useState("");
    const [evisitSymptomsList, setevisitSymptomsList] = useState([]);    
    const nextButtonClass = useNextButtonWidthClass();
    const { isMobile } = useDetectMobile();

    // Returns a stable key for a symptom array by sorting entries, used to compare whole combinations.
    const getSymptomsKey = (symptoms) => {
        if (!Array.isArray(symptoms) || !symptoms.length) return '';
        return JSON.stringify([...symptoms].sort((a, b) => a.id.localeCompare(b.id)));
    };

    // Reset submitted state and store the entire invalid symptom combination (not individual symptoms).
    useEffect(() => {
        if (symptomError) {
            setSubmitted(false);
            setEditing(true);

            if (symptomsList.length) {
                const currentKey = getSymptomsKey(symptomsList);

                setInvalidSymptomsList((currentInvalidSymptoms) => {
                    const alreadyStored = currentInvalidSymptoms.some(
                        (combination) => getSymptomsKey(combination) === currentKey
                    );
                    if (alreadyStored) return currentInvalidSymptoms;
                    return [...currentInvalidSymptoms, [...symptomsList]];
                });

                if (!invalidSymptomsMessage && symptomError?.message) {
                    setInvalidSymptomsMessage(symptomError.message);
                }
            }
        }
    }, [symptomError, symptomsList, invalidSymptomsMessage]);

    const headerText = message?.data?.[0]?.headerText
        ? message.data[0].headerText
        : '';

    const instructions = (message?.text)
        ? message.text
        : '';

    const searchTab = message?.data?.[0]?.searchHeaderText
        ? message.data[0].searchHeaderText
        : '';

    const bodyTab = message?.data?.[0]?.bodyHeaderText
        ? message.data[0].bodyHeaderText
        : '';

    const questionId = (message?.data?.[0]?.question_id)
        ? message.data[0].question_id
        : ''

    useEffect(() => {
        setInvalidSymptomsList([]);
        setInvalidSymptomsMessage('');
        setEditing(false);
    }, [questionId]);

    const currentKey = getSymptomsKey(symptomsList);
    const hasOnlyInvalidSymptomsSelected = {
        isInvalid: !!symptomsList.length && invalidSymptomsList.some(
            (combination) => getSymptomsKey(combination) === currentKey
        ),
        message: invalidSymptomsMessage || symptomError?.message || ''
    };
    
    const nextButton =(message.buttons?.[0].displayText)
    ? message.buttons?.[0].displayText
    : ''

    const toastCloseText = (message?.buttons?.[3]?.displayText)
        ? message.buttons[3].displayText
        : '';

    const isDisabled = submitted || disabled;
    const isSubmittionDisabled = submitted || !symptomsList.length || isDisabled || hasOnlyInvalidSymptomsSelected.isInvalid;
    
    const addSymptomTag = (value) => {
        setSymptomsList(getUniqueSymptoms(value));
        setToastMessage(null);
        if (symptomError && setSymptomError) setSymptomError(null);
    };

    const addMobileSymptomTag = (value) => {
        setSymptomsList(getUniqueSymptoms(value));
        if (symptomError && setSymptomError) setSymptomError(null);
    };

    const sendEvisits = (value) => {
        setevisitSymptomsList(value);
        //setSubmitted(true);
    };

    useEffect(() => {
        if (evisitSymptomsList.length > 0) {
            setSubmitted(true);
            editing? sendMessage('message', `##edit@${message.data[0].question_id}@${JSON.stringify(evisitSymptomsList)}`,`##edit@${message.data[0].question_id}@${evisitSymptomsList}`):sendMessage('message', JSON.stringify(evisitSymptomsList), evisitSymptomsList);
        }
    }, [evisitSymptomsList]);

    const { logTiming } = useTiming();
    const cardName = (message.data && message.data[0] && message.data[0].question_id) ? message.data[0].question_id : "Symptoms";
     useCardLoadTiming({
                  sentAt: message._sentAt,
                  cardName,
                  logTiming,
                  formatCardLoadTiming
              });

    const submitPress = (event) => {
      if (!symptomsList.length) return;

        if (submitted) {
            sendMessage('rewind', questionId, questionId);
            setSubmitted(false);
            setEditing(true);           
            return;
        }

        setSubmitted(true);

        var retSymptoms = []

        symptomsList.forEach(symptom => {
            retSymptoms.push({
                name: symptom.name,
                id: symptom.id
            })
        })
        
        editing
            ? sendMessage('message', `##edit@${message.data[0].question_id}@${JSON.stringify(retSymptoms)}`, `##edit@${message.data[0].question_id}@${retSymptoms}`, cardName)
            :sendMessage('message', JSON.stringify(retSymptoms), retSymptoms, cardName);
    };

    const showToast = (tab) => {
        setToastMessage(
            {
                type: 'symptoms',
                fullscreen: true,
                header: '',
                body: (
                    <MobileSymptomSearchToast
                        initialTab={tab}
                        message={message}
                        initialTags={symptomsList}
                        onTagsChange={addMobileSymptomTag}
                        searchTab={searchTab}
                        bodyTab={bodyTab}
                        bodyPartHover={bodyPartHover}
                        setBodyPartHover={setBodyPartHover}
                        disabled={isDisabled}
                        HoverColor={HoverColor}
                        infermedicaAppIdKey={infermedicaAppIdKey}
                        evisits={evisitSymptomsList}
                        setevisits={sendEvisits}
                        setToastMessage={setToastMessage}
                        symptomError={symptomError}
                        hasOnlyInvalidSymptomsSelected={hasOnlyInvalidSymptomsSelected}
                    />
                ),
                closeText: toastCloseText,
                // Overrides the default focus
                focus: () => { document.querySelector('#iphone-focus-hack')?.click();  }
            }
        );
    }

    return (
        <div className={
            (isDisabled ? 'disabled ' : '') +
            (isMobile ? 'mobile ' : 'desktop ') +
            'symptom-search card'}
        >
            <div className={'content'}>
                <div
                    className='input-blocker'
                    style={submitted ? { display: "block" } : { display: "none" }}
                />
                {
                    isMobile &&
                    <>
                        <div className='header'>{getMarkDown(headerText)}</div>
                        <div className='instructions'>{getMarkDown(instructions)}</div>
                    </>
                }
                {
                    isMobile
                        ?
                        <div>
                            <div className='tabbar'>
                                <div className={'selected tab'}                               >
                                    {searchTab}
                                </div>
                                <div
                                    className={'tab'}
                                    onClick={() => showToast(1)}
                                >
                                    {bodyTab}
                                </div>
                            </div>
                            <SymptomSearchText message={message} showToast={() => showToast(0)} tags={symptomsList} setTags={addSymptomTag} setBodyPartHover={setBodyPartHover} disabled={isDisabled} HoverColor={HoverColor} evisits={evisitSymptomsList} setevisits={sendEvisits} setToastMessage={setToastMessage} symptomError={symptomError} hasOnlyInvalidSymptomsSelected={hasOnlyInvalidSymptomsSelected}/>
                        </div>

                        : // is Desktop
                        <div>
                            <SymptomSearchText message={message} tags={symptomsList} setTags={addSymptomTag} setBodyPartHover={setBodyPartHover} disabled={isDisabled} HoverColor={HoverColor} evisits={evisitSymptomsList} setevisits={sendEvisits} setToastMessage={setToastMessage} symptomError={symptomError} hasOnlyInvalidSymptomsSelected={hasOnlyInvalidSymptomsSelected}/>
                            <SymptomSearchBody message={message} tags={symptomsList} setTags={addSymptomTag} bodyPartHover={bodyPartHover} setBodyPartHover={setBodyPartHover} disabled={isDisabled} HoverColor={HoverColor}/>
                        </div>
                }
            </div>
            <Footer
                showChangeButton={questionId && submitted && !message.isLastMessage}
                onAccepted={submitPress}
                onDenied={() => sendMessage('fastforward')}
                disabled={disabled}
                divID={questionId}
                message={message}
            >
                    <button
                        className={`submit-button ${nextButtonClass} ${isSubmittionDisabled ? 'next-disabled' : ''}`.trim()}
                        onClick={submitPress}
                        disabled={isSubmittionDisabled}
                        style={{'visibility': submitted ? 'hidden': '', 'display': submitted ? 'none' : ''}}
                        aria-hidden={isSubmittionDisabled ? 'true': 'false'}
                    >
                        {getButtonWithArrow(nextButton)}
                    </button>                
            </Footer>
        </div>
    );
};

export default SymptomSearchMessage;